import { useState } from 'react'
import { X } from 'lucide-react'
import type { CreateManualLeadRequest, LandingLeadDto } from '@impulso/shared/api/tracking'
import { errorMessage } from '../../lib/api'
import { useToast } from '../../ui/toast'
import { useCreateManualLead } from './api'
import { fmtDateTime } from './components'

export default function ManualLeadModal({ companyId, onClose, onUseExisting }: {
  companyId: string
  onClose: () => void
  onUseExisting: (lead: LandingLeadDto) => void
}) {
  const create = useCreateManualLead(companyId)
  const toast = useToast()
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [message, setMessage] = useState('')
  const [consent, setConsent] = useState(false)
  const [duplicates, setDuplicates] = useState<LandingLeadDto[]>([])
  const [confirmingDuplicate, setConfirmingDuplicate] = useState(false)

  const submit = (allowSamePhone = false) => {
    const body: CreateManualLeadRequest = {
      name: name.trim() || undefined,
      phone,
      message: message.trim() || undefined,
      whatsappConsent: consent,
      ...(allowSamePhone ? { allowSamePhone: true } : {}),
    }
    create.mutate(body, {
      onSuccess: (result) => {
        if (result.status === 'duplicates') {
          setDuplicates(result.items)
          setConfirmingDuplicate(false)
          return
        }
        toast('Contato cadastrado.')
        onClose()
      },
      onError: (e) => toast(errorMessage(e)),
    })
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <form className="modal" role="dialog" aria-modal="true" aria-labelledby="new-contact-title" onClick={(e) => e.stopPropagation()} onSubmit={(event) => { event.preventDefault(); submit() }}>
        <div className="modal-head"><h2 id="new-contact-title">Novo contato do WhatsApp</h2><button type="button" className="icon-btn" style={{ marginLeft: 'auto' }} aria-label="Fechar" onClick={onClose}><X size={17} /></button></div>
        <div className="modal-body stack" style={{ gap: 12 }}>
          <div className="field"><label htmlFor="contact-name">Nome</label><input id="contact-name" className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} autoFocus /></div>
          <div className="field"><label htmlFor="contact-phone">Telefone com DDD</label><input id="contact-phone" className="input" inputMode="tel" autoComplete="tel" placeholder="(11) 99999-9999" value={phone} onChange={(e) => { setPhone(e.target.value); setDuplicates([]); setConfirmingDuplicate(false) }} required /></div>
          <div className="field"><label htmlFor="contact-message">Anotação do atendimento</label><textarea id="contact-message" className="input" rows={3} maxLength={1000} value={message} onChange={(e) => setMessage(e.target.value)} /></div>
          <label className="row small" style={{ gap: 8, alignItems: 'flex-start' }}><input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} /><span>Confirmei que a pessoa autorizou futuras mensagens por WhatsApp.</span></label>
          <p className="small muted">Cadastrar ou abrir o contato não envia mensagens nem registra autorização automaticamente. Você revisa e envia pelo próprio WhatsApp.</p>
          {!!create.error && <div className="form-error" role="alert">{errorMessage(create.error)}</div>}
          {duplicates.length > 0 && <DuplicateChoices matches={duplicates} confirming={confirmingDuplicate} onUse={onUseExisting} onConfirmNew={() => setConfirmingDuplicate(true)} onCreateNew={() => submit(true)} onCancelCreate={() => setConfirmingDuplicate(false)} pending={create.isPending} />}
        </div>
        <div className="modal-foot">
          <button type="button" className="btn ghost" onClick={onClose}>Cancelar</button>
          {duplicates.length === 0 && <button className="btn primary" disabled={create.isPending}>{create.isPending ? 'Verificando…' : 'Cadastrar contato'}</button>}
        </div>
      </form>
    </div>
  )
}

function DuplicateChoices({ matches, confirming, onUse, onConfirmNew, onCreateNew, onCancelCreate, pending }: {
  matches: LandingLeadDto[]
  confirming: boolean
  onUse: (lead: LandingLeadDto) => void
  onConfirmNew: () => void
  onCreateNew: () => void
  onCancelCreate: () => void
  pending: boolean
}) {
  return (
    <section className="card stack" aria-live="polite" style={{ gap: 10 }}>
      <div>
        <b>{matches.length === 1 ? 'Já existe um contato com este telefone.' : `Encontramos ${matches.length} contatos com este telefone.`}</b>
        <p className="small muted">Escolha qual registro usar. Nenhuma anotação ou autorização será alterada.</p>
      </div>
      {matches.map((lead) => (
        <article className="card stack" key={lead.id} style={{ gap: 6 }}>
          <div className="row between wrap" style={{ gap: 8 }}>
            <div><b>{lead.name ?? 'Sem nome'}</b><div className="small muted">{lead.source ?? 'Sem origem'} · {fmtDateTime(lead.createdAt)} · {lead.status === 'new' ? 'Novo' : 'Respondido'}</div></div>
            <button type="button" className="btn sm" onClick={() => onUse(lead)}>Usar este contato</button>
          </div>
          {lead.message && <details className="small"><summary>Anotação existente</summary><p>{lead.message}</p></details>}
          <span className="small muted">Autorização WhatsApp: {lead.whatsappConsent ? 'registrada' : 'não registrada'}</span>
        </article>
      ))}
      {!confirming ? (
        <button type="button" className="btn sm" onClick={onConfirmNew}>Criar outro contato com este telefone…</button>
      ) : (
        <div className="card stack" role="alert" style={{ gap: 8 }}>
          <p className="small">Isso criará um cadastro separado para o mesmo número. Os registros existentes serão preservados sem alterações.</p>
          <div className="row wrap" style={{ gap: 8 }}>
            <button type="button" className="btn ghost sm" onClick={onCancelCreate}>Voltar</button>
            <button type="button" className="btn primary sm" disabled={pending} onClick={onCreateNew}>{pending ? 'Salvando…' : 'Confirmar novo contato'}</button>
          </div>
        </div>
      )}
    </section>
  )
}
